import { useMemo } from "react";
import { useAppSelector } from "@/hooks";
import SummaryPanel from "@/pages/checkout/components/ConfirmationView/components/SummaryPanel";
import { RootState } from "@/store/store";
import { getOrderDetails, getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { IPaymentForm } from "./AdminPayment";
import DataCell from "@/components/DataCell";
import { formatPhone } from "@/utils/phones";
import { paymentMethodsLabels } from "@/constants/paymentMethods";
import { AnonymousUser, Cash, Location, OrderItem, PaymentMethodsType, User } from "@/types";
import { addCurrency } from "@/utils/numbers";
import Price from "@/components/Price";

export type ICheckout = {
  client: Partial<User | AnonymousUser>;
  deliveryLocation: Location;
  deliveryFee?: number;
  scheduleTime?: Date | null;
} & IPaymentForm;

const AdminCheckout = () => {
  const order = useAppSelector((state: RootState) => state.order);
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const organization = useAppSelector((store) => store.organizations[organizationId ?? ""] ?? null);
  const { values } = useFormikContext<ICheckout>();

  const paymentMethod = useMemo(() => {
    if (!values.paymentDetails) return "";
    const methodKey = String(values.paymentDetails.method) as keyof typeof paymentMethodsLabels;
    return paymentMethodsLabels[methodKey] ?? "";
  }, [values.paymentDetails]);

  return (
    <>
      <Stack spacing={3}>
        <SummaryPanel title="¿Qué recibirás?">
          {order?.items?.map((item: any) => (
            <Stack key={item.id} spacing={2} direction={"row"}>
              <Stack flex={1}>
                <Typography fontWeight={600}>
                  {item.quantity} - {item.title}
                </Typography>
                <Typography variant="body1">{getOrderDetails(item)}</Typography>
                {item?.comments ? <Typography variant="body1">Comentarios: {item?.comments}</Typography> : null}
              </Stack>
            </Stack>
          ))}
        </SummaryPanel>

        {values?.scheduleTime ? (
          <SummaryPanel title="¿Cuándo recoger el pedido?">
            <DataCell label="Horario" value={new Date(values.scheduleTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
          </SummaryPanel>
        ) : (
          <SummaryPanel title="¿Dónde enviar tu pedido?">
            {/* <DataCell label="Barrio" value={values?.deliveryLocation?.neighborhood ?? ""} /> */}
            <DataCell label="No. casa" value={values?.deliveryLocation?.house ?? ""} />
            <DataCell label="Dirección" value={values?.deliveryLocation?.address ?? ""} />
          </SummaryPanel>
        )}

        <SummaryPanel title="¿Quién recibirá el pedido?">
          <DataCell label="Nombre" value={`${values?.client?.name ?? ""} ${values?.client?.lastname ?? ""}`} />
          <DataCell label="Teléfono" value={formatPhone(values?.client?.phone, 505) ?? ""} />
          <DataCell label="Email" value={values?.client?.email ?? ""} />
        </SummaryPanel>
        <SummaryPanel title="¿Cómo deseas pagar?">
          <DataCell label="Método de pago" value={(paymentMethod || values?.paymentDetails?.method) ?? ""} />

          {values.paymentDetails?.value ? (
            (() => {
              const val = values?.paymentDetails?.value as any;

              if (val && (val?.bankName || val?.accountNumber || val?.account_number)) {
                return (
                  <>
                    <DataCell label="Banco" value={val?.bankName ?? val?.bank ?? ""} />
                    <DataCell label="No. de Cuenta" value={val?.accountNumber ?? val?.account_number ?? ""} />
                    <DataCell label="Moneda" value={val?.currency ?? ""} />
                  </>
                );
              }

              return values.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD ? (
                <DataCell
                  label="Tarjeta"
                  value={val?.cardBrand ?? val?.brand ?? val?.maskedNumber ?? val?.last4 ?? val?.label ?? (typeof val === "string" ? val : JSON.stringify(val))}
                />
              ) : (
                <DataCell label="Cuánto paga el cliente" value={<Price currency="NIO">{(val as Cash)?.amount}</Price>} />
              );
            })()
          ) : (
            <></>
          )}
        </SummaryPanel>
        <SummaryPanel title="Resumen de costos">
          <Stack spacing={1}>
            <DataCell label="Subtotal" value={<Price currency="NIO">{getOrderSubtotal(order.items as OrderItem[])}</Price>} />
            {values?.deliveryFee ? <DataCell label="Envío" value={addCurrency(values.deliveryFee, "NIO")} /> : null}

            {order?.taxes && organization?.settings?.includeTax ? (
              <DataCell label="IVA" value={<Price currency="NIO">{getOrderTaxes(order.items)}</Price>} />
            ) : (
              <DataCell label="IVA" value={<Price currency="NIO">0</Price>} />
            )}

            <DataCell label="Total" value={<Price currency="NIO">{getOrderTotal(order.items, organization?.settings?.includeTax, Number(values?.deliveryFee) || 0)}</Price>} />
          </Stack>
        </SummaryPanel>
      </Stack>
    </>
  );
};

export default AdminCheckout;
